import type { PractitionerFormField } from '../types/firestore/practitionerForm';

const trimString = (raw: unknown): string =>
  typeof raw === 'string' ? raw.trim() : '';

const stringArrayAnswer = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item): item is string => typeof item === 'string')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }
    } catch {
      // fall through
    }
  }
  return [];
};

const answerMatchesEquals = (
  controlling: PractitionerFormField,
  raw: unknown,
  expected: string,
): boolean => {
  const want = expected.trim();
  if (want.length === 0) {
    return false;
  }
  if (controlling.type === 'checkboxes') {
    return stringArrayAnswer(raw).includes(want);
  }
  return trimString(raw) === want;
};

/**
 * Whether `field` should be shown, given current answers and which fields are already visible.
 * When the controlling field is not visible yet, this returns false (iterative visibility).
 */
export const isPractitionerFieldVisibleInWave = (props: {
  field: PractitionerFormField;
  controllingField: PractitionerFormField | undefined;
  rawAnswer: unknown;
  controllingFieldId: string;
  visibleSoFar: ReadonlySet<string>;
}): boolean => {
  const sw = props.field.showWhen;
  if (!sw) {
    return true;
  }
  if (!props.visibleSoFar.has(props.controllingFieldId)) {
    return false;
  }
  if (!props.controllingField) {
    return false;
  }
  return answerMatchesEquals(
    props.controllingField,
    props.rawAnswer,
    sw.equals,
  );
};

/**
 * Resolves which fields are visible (supports chains: A → B → C).
 */
export const computeVisiblePractitionerFieldIds = (props: {
  fields: PractitionerFormField[];
  rawAnswers: Record<string, unknown>;
}): Set<string> => {
  const { fields, rawAnswers } = props;
  const byId = new Map(fields.map((f) => [f.id, f]));
  const visible = new Set<string>();
  for (const f of fields) {
    if (!f.showWhen) {
      visible.add(f.id);
    }
  }
  let added = true;
  while (added) {
    added = false;
    for (const f of fields) {
      if (visible.has(f.id) || !f.showWhen) {
        continue;
      }
      const ctlId = f.showWhen.fieldId;
      const ctl = byId.get(ctlId);
      if (
        isPractitionerFieldVisibleInWave({
          field: f,
          controllingField: ctl,
          rawAnswer: rawAnswers[ctlId],
          controllingFieldId: ctlId,
          visibleSoFar: visible,
        })
      ) {
        visible.add(f.id);
        added = true;
      }
    }
  }
  return visible;
};
